import shopApi from '@services/shop';
import { usePolling } from './usePolling';
import { CONNECTION_KIND, CONNECTION_WARNINGS } from '../constants';

/**
 * Polls for stock and product connection status
 * Checks for successful inventory ingestion and triggers callbacks
 *
 * @param {boolean} pollingForStockConnection - Whether to poll for stock connection
 * @param {boolean} pollingForProductConnection - Whether to poll for product connection
 * @param {Object} currentShop - Current shop object
 * @param {Function} onShopRefresh - Called when shop data needs refreshing
 * @param {Function} setPollingForStockConnection - Sets stock polling state
 * @param {Function} setPollingForProductConnection - Sets product polling state
 * @param {Function} setStockConnectionWarning - Sets stock warning message
 * @param {Function} setProductConnectionWarning - Sets product warning message
 */
export const useConnectionPolling = (
	pollingForStockConnection,
	pollingForProductConnection,
	currentShop,
	onShopRefresh,
	setPollingForStockConnection,
	setPollingForProductConnection,
	setStockConnectionWarning,
	setProductConnectionWarning
) => {
	// Poll for stock connection
	usePolling({
		enabled: pollingForStockConnection && !!currentShop,
		fetchFn: async () => {
			const shops = await shopApi.getMyShops();
			return shops.find((s) => s.id === currentShop.id);
		},
		successCondition: (shop) => {
			const stockSource = shop?.inventorySources?.find(
				(source) => source.kind === CONNECTION_KIND.STOCK
			);
			return !!stockSource?.latestIngest?.timestamp;
		},
		onSuccess: async () => {
			await onShopRefresh();
			setPollingForStockConnection(false);
		},
		onTimeout: () => {
			setPollingForStockConnection(false);
			setStockConnectionWarning(CONNECTION_WARNINGS.STOCK_TIMEOUT);
		},
	});

	// Poll for product connection
	usePolling({
		enabled: pollingForProductConnection && !!currentShop,
		fetchFn: async () => {
			const shops = await shopApi.getMyShops();
			return shops.find((s) => s.id === currentShop.id);
		},
		successCondition: (shop) => {
			const productSource = shop?.inventorySources?.find(
				(source) => source.kind === CONNECTION_KIND.PRODUCT
			);
			return !!productSource?.latestIngest?.timestamp;
		},
		onSuccess: async () => {
			await onShopRefresh();
			setPollingForProductConnection(false);
		},
		onTimeout: () => {
			setPollingForProductConnection(false);
			setProductConnectionWarning(CONNECTION_WARNINGS.PRODUCT_TIMEOUT);
		},
	});
};
